/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'

import {
  DealNode,
  DealsDealStatusChoices,
  useDealQuery,
} from '@/apollo/graphql'
import { homePath, transformData } from '@/utils'
import { IDeal, TFormSubmit } from '@/types'
import { useAPI } from '@/hooks'
import moment from 'moment'

interface IUseData {
  id?: string
}

interface IUseDataRes {
  loading: boolean
  submitting: boolean
  data: Partial<DealNode> | undefined
  onSubmit: TFormSubmit<IDeal>
  onCancel: () => void
}

const defaultData: Partial<IDeal> = {
  status: DealsDealStatusChoices.InCreation,
  npv: null,
}

export const useData = ({ id }: IUseData): IUseDataRes => {
  const navigate: NavigateFunction = useNavigate()
  const { submitting, sendForm } = useAPI()
  const [data, setData] = useState<Partial<IDeal>>(defaultData)

  const { loading, refetch } = useDealQuery({
    skip: !id,
    fetchPolicy: 'network-only',
    variables: {
      id: id || '',
    },
    onCompleted: res => {
      if (res.deal) {
        // @ts-ignore
        setData({
          ...(res.deal || {}),
          status: res.deal.status || DealsDealStatusChoices.InCreation,
        })
      }
    },
  })

  useEffect(() => {
    if (!id) {
      setData(defaultData)
    }
  }, [id])

  const onSubmit: TFormSubmit<IDeal> = useCallback(
    (path, isEdit, callback) => d => {
      if (d.startDate) {
        d.startDate = moment(d.startDate).format('YYYY-MM-DD')
      }
      if (d.endDate) {
        d.endDate = moment(d.endDate).format('YYYY-MM-DD')
      }

      sendForm(
        {
          path,
          method: isEdit ? 'PUT' : 'POST',
          data: transformData({
            ...d,
            npv: (d.npv as unknown) === '' || d.npv == null ? null : d.npv,
          }),
          callback: e => {
            callback?.(e)
            refetch()
          },
        },
        { showNotify: true },
      )
    },
    [refetch, sendForm],
  )

  const onCancel = useCallback(() => {
    navigate(homePath)
  }, [navigate])

  return {
    loading,
    submitting,
    data,
    onSubmit,
    onCancel,
  }
}
